import { expenseBlockers } from '@wr/shared';
import { ApiError, get, putFile } from '../../api.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';
import { isUnreachable } from '../people/data.js';

/** A receipt as the list and the sheet show it. */
export interface Expense {
  id: string;
  vendor: string | null;
  spent_on: string | null;
  currency: string | null;
  total: number | null;
  tax_gst?: number | null;
  tax_qst?: number | null;
  tax_vat?: number | null;
  budget_line: string | null;
  event_id: string | null;
  event_code?: string | null;
  client: string | null;
  purpose?: string | null;
  guests?: string | null;
  reference: string | null;
  sent_at: string | null;
  sent_to?: string | null;
  send_error: string | null;
  has_receipt: boolean;
  receipt_expected: boolean;
  submitted_by?: string | null;
  created_by?: string;
  created_at: string | null;
  /** Only on this device so far. */
  local?: boolean;
}

export interface ExpenseQuery {
  q: string;
  period: '7' | '30' | '90';
  event: string; // an event ID, 'none' or 'all'
  budget: string;
  page: number;
}

export interface ExpensePage {
  items: Expense[];
  total: number;
  totals: { currency: string; total: number }[];
  page: number;
  pageSize: number;
  offline?: boolean;
}

/** What the AI read on a receipt: to check and complete. */
export interface ReceiptReading {
  vendor: string | null;
  spent_on: string | null;
  currency: string | null;
  total: number | null;
  tax_gst: number | null;
  tax_qst: number | null;
  tax_vat: number | null;
  budget_line: string | null;
  purpose: string | null;
}

export function readReceipt(photo: Blob): Promise<ReceiptReading> {
  return putFile<ReceiptReading>('/expenses/read', photo, 'POST');
}

const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

/** A receipt kept on this device, as the list shows it. */
export function fromRecord(r: LocalRecord, events: LocalRecord[]): Expense {
  return {
    id: r.id,
    vendor: str(r.vendor),
    spent_on: str(r.spent_on)?.slice(0, 10) ?? null,
    currency: str(r.currency),
    total: num(r.total),
    tax_gst: num(r.tax_gst),
    tax_qst: num(r.tax_qst),
    tax_vat: num(r.tax_vat),
    budget_line: str(r.budget_line),
    event_id: str(r.event_id),
    event_code: (events.find((e) => e.id === r.event_id)?.event_code as string | undefined) ?? null,
    client: str(r.client),
    purpose: str(r.purpose),
    guests: str(r.guests),
    reference: str(r.reference),
    sent_at: str(r.sent_at),
    sent_to: str(r.sent_to),
    send_error: str(r.send_error),
    has_receipt: Boolean(r.receipt_key),
    receipt_expected: r.receipt_expected === true,
    created_by: str(r.created_by) ?? undefined,
    created_at: str(r.created_at),
    local: !r.created_at,
  };
}

const PAGE_SIZE = 20;

/** Recent receipts: the portal's list; without network, those this device keeps. */
export async function listExpenses(
  query: ExpenseQuery,
  store: LocalStore | null,
  visibleDays = 90,
): Promise<ExpensePage> {
  const p = new URLSearchParams({
    period: query.period,
    event: query.event,
    page: String(query.page),
  });
  if (query.q.trim()) p.set('q', query.q.trim());
  if (query.budget) p.set('budget', query.budget);
  try {
    const page = await get<ExpensePage>(`/expenses?${p.toString()}`);
    // Receipts saved here and not received yet show first.
    if (!store || query.page > 1) return page;
    const waiting = await waitingHere(
      store,
      page.items.map((i) => i.id),
    );
    return waiting.length ? { ...page, items: [...waiting, ...page.items] } : page;
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
  }
  const [records, events] = await Promise.all([
    store.listRecords('expense'),
    store.listRecords('event'),
  ]);
  const from = new Date(Date.now() - Number(query.period) * 86_400_000).toISOString().slice(0, 10);
  const cut = new Date(Date.now() - visibleDays * 86_400_000).toISOString();
  const words = query.q.toLowerCase().split(/\s+/).filter(Boolean);
  const all = records
    .filter((r) => !r.deleted)
    .map((r) => fromRecord(r, events))
    .filter((x) => !x.sent_at || x.sent_at >= cut)
    .filter((x) => !x.spent_on || x.spent_on >= from)
    .filter(
      (x) =>
        query.event === 'all' ||
        (query.event === 'none' ? !x.event_id : x.event_id === query.event),
    )
    .filter((x) => !query.budget || x.budget_line === query.budget)
    .filter((x) =>
      words.every((w) =>
        [x.vendor, x.client, x.purpose, x.guests].join(' ').toLowerCase().includes(w),
      ),
    )
    .sort((a, b) => (b.spent_on ?? '9999').localeCompare(a.spent_on ?? '9999'));
  const sums = new Map<string, number>();
  for (const x of all) {
    const c = x.currency ?? 'CAD';
    sums.set(c, Math.round(((sums.get(c) ?? 0) + (x.total ?? 0)) * 100) / 100);
  }
  return {
    items: all.slice((query.page - 1) * PAGE_SIZE, query.page * PAGE_SIZE),
    total: all.length,
    totals: [...sums]
      .map(([currency, total]) => ({ currency, total }))
      .sort((a, b) => a.currency.localeCompare(b.currency)),
    page: query.page,
    pageSize: PAGE_SIZE,
    offline: true,
  };
}

/** Receipts saved on this device whose change has not reached the portal yet. */
async function waitingHere(store: LocalStore, known: string[]): Promise<Expense[]> {
  const ids = new Set(
    (await store.outbox())
      .map((i) => i.op)
      .filter((op) => op.entity === 'expense' && op.action === 'upsert')
      .map((op) => (op as { id: string }).id),
  );
  if (ids.size === 0) return [];
  const events = await store.listRecords('event');
  const out: Expense[] = [];
  for (const id of ids) {
    if (known.includes(id)) continue;
    const r = await store.getRecord('expense', id);
    if (r && !r.deleted) out.push({ ...fromRecord(r, events), local: true });
  }
  return out;
}

/** One receipt: the portal's; a receipt not sent from this device yet, the device's. */
export async function loadExpense(id: string, store: LocalStore | null): Promise<Expense | null> {
  try {
    return await get<Expense>(`/expenses/${encodeURIComponent(id)}`);
  } catch (error) {
    const missing = error instanceof ApiError && error.status === 404;
    if (!store || !(missing || isUnreachable(error))) throw error;
  }
  const r = await store.getRecord('expense', id);
  if (!r || r.deleted) return null;
  return { ...fromRecord(r, await store.listRecords('event')), local: true };
}

/** What blocks the sending: the required fields of brief section 7.8. */
export const documented = (x: Record<string, unknown>) => expenseBlockers(x).length === 0;
